import React, { useState, useEffect, useRef } from "react";
import { PhoneInput, CountrySelect } from "../components/PhoneInput";
import { detectCountryFromIP } from "../data/countries";
import { Link } from "../components/L";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "../i18n/LanguageContext";
import { X } from "lucide-react";
import useSEO from "../hooks/useSEO";
import Lightbox from "../components/Lightbox";
import Modal from "../components/Modal";
import { formatPrice } from "../utils/price";

// tx-catalog-start — payment method labels returned by /api/payment-methods/public: "Karte krediti/debiti", "Transferte bankare", "Pagese cash"
// tx-catalog-end
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// --- Pricing tier helpers (mirror backend logic) ---
const parseDDMMYYYY = (s) => {
  if (!s) return null;
  const parts = String(s).trim().split("/");
  if (parts.length !== 3) return null;
  const dayPart = parts[0].split("-").pop();
  const d = parseInt(dayPart, 10);
  const m = parseInt(parts[1], 10);
  const y = parseInt(parts[2], 10);
  if (!d || !m || !y) return null;
  const dt = new Date(y, m - 1, d);
  return isNaN(dt.getTime()) ? null : dt;
};
const getActiveTier = (event) => {
  const tiers = (event && event.pricing_tiers) || [];
  if (!tiers.length) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const active = tiers.filter((t) => {
    const start = parseDDMMYYYY(t.start_date);
    const end = parseDDMMYYYY(t.end_date);
    if (start && end) {
      const endEod = new Date(end); endEod.setHours(23, 59, 59, 999);
      return start <= today && today <= endEod;
    }
    return !start && !end;
  });
  if (!active.length) return null;
  const num = (t) => {
    const n = parseFloat(String(t.price || "0").replace(",", "."));
    return isNaN(n) ? Infinity : n;
  };
  return [...active].sort((a, b) => num(a) - num(b))[0];
};
const getDisplayPrice = (event) => {
  const t = getActiveTier(event);
  if (t && (t.price ?? "") !== "") return String(t.price);
  return String(event?.price ?? "");
};
const isTierActive = (event, tier) => {
  const active = getActiveTier(event);
  return active && active === tier;
};
const isTierWithinRange = (tier) => {
  if (!tier) return false;
  const start = parseDDMMYYYY(tier.start_date);
  const end = parseDDMMYYYY(tier.end_date);
  if (!start && !end) return true; // no bounds => always selectable
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (start && today < start) return false;
  if (end) {
    const endEod = new Date(end); endEod.setHours(23, 59, 59, 999);
    if (today > endEod) return false;
  }
  return true;
};


const FreeRegistrationPopup = ({ event, selectedTier = null, onClose }) => {
  const { t, tx } = useLanguage();
  const [form, setForm] = useState({ first_name: "", last_name: "", profession: "", phone: "", phone_country: "al", email: "", city: "", country: "al", badge_photo: "", accepted_terms: false, marketing_consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    let cancelled = false;
    detectCountryFromIP().then((c) => {
      if (!cancelled && c) setForm((f) => ({ ...f, phone_country: c, country: c }));
    });
    return () => { cancelled = true; };
  }, []);

  const handleBadgeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const okType = ["image/jpeg", "image/jpg", "image/pjpeg"].includes(file.type);
    if (!okType || !["jpg", "jpeg"].includes(ext)) {
      setError(tx("Vetem foto JPG/JPEG lejohen."));
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) { setError(tx("Foto max 5MB")); return; }
    setUploadingPhoto(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload?private=true`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, badge_photo: res.data.url }));
    } catch (err) {
      setError(tx("Ngarkimi i fotos deshtoi. Provo perseri."));
    } finally { setUploadingPhoto(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.first_name || !form.last_name || !form.email) {
      setError(t("reg_required"));
      return;
    }
    const localDigits = form.phone.replace(/^\+\d+\s*/, "").replace(/\D/g, "");
    if (!form.phone || localDigits.length < 5) {
      setError(tx("Ju lutem plotesoni nje numer telefoni te vlefshem."));
      return;
    }
    if (!form.badge_photo) {
      setError(tx("Ju lutem ngarkoni foton per badge (JPG/JPEG)."));
      return;
    }
    if (!form.accepted_terms) {
      setError(tx("Ju lutem pranoni Kushtet e Perdorimit dhe Politiken e Privatesise per te vazhduar."));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/registrations`, {
        ...form,
        event_id: event.id,
        num_participants: 1,
        tier_label: selectedTier?.label || "",
        tier_price: selectedTier?.price || "",
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || tx("Ndodhi nje gabim."));
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <Modal onClose={onClose} maxWidth="max-w-md" testId="reg-success-popup">
        <div className="p-8 text-center">
          <div className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ background: "linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)" }}>
            <i className="fas fa-check text-2xl text-white"></i>
          </div>
          <h3 className="text-xl font-bold mb-3" style={{ color: "#08707A", fontFamily: "'Noto Sans', sans-serif" }}>{t("reg_success_title")}</h3>
          <p className="text-[#4A5568] mb-6">{tx("Regjistrimi per eventin")} <strong>"{event.title}"</strong> {tx("u krye me sukses. Do t'ju kontaktojme se shpejti.")}</p>
          <button onClick={onClose} className="rounded-lg px-8 py-2.5 text-sm font-semibold text-white" style={{ background: "linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)" }} data-testid="close-success-popup">
            {tx("Mbylle")}
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} maxWidth="max-w-md" testId="reg-popup">
      <div className="flex items-center justify-between p-6 border-b border-slate-200">
        <div>
          <h3 className="text-lg font-bold" style={{ fontFamily: "'Noto Sans', sans-serif" }}>{t('register_now')}</h3>
          <p className="text-xs text-[#828D9E] mt-0.5">{event.title}</p>
          {selectedTier && (
            <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-[#1CA3B0]/10 border border-[#1CA3B0]/20 px-2.5 py-1" data-testid="popup-selected-tier">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#08707A]">{selectedTier.label}</span>
              {selectedTier.price && (
                <span className="text-xs font-bold text-[#08707A]">{formatPrice(selectedTier.price)}</span>
              )}
            </div>
          )}
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
      </div>
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#4A5568] mb-1">{t("reg_first_name")} *</label>
              <input type="text" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 focus:ring-2 focus:ring-[#3CD4E1]/30 focus:border-[#3CD4E1] outline-none" data-testid="popup-first-name" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#4A5568] mb-1">{t("reg_last_name")} *</label>
              <input type="text" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 focus:ring-2 focus:ring-[#3CD4E1]/30 focus:border-[#3CD4E1] outline-none" data-testid="popup-last-name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#4A5568] mb-1">{tx("Profesioni")}</label>
              <input type="text" value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} placeholder={tx("psh. Dentist, Ortodont")} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 focus:ring-2 focus:ring-[#3CD4E1]/30 focus:border-[#3CD4E1] outline-none" data-testid="popup-profession" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#4A5568] mb-1">{tx("Foto per Badge *")}</label>
              <div className="flex items-center gap-2">
                <label className="flex-1 cursor-pointer min-w-0">
                  <div className={`border border-dashed border-slate-300 rounded-lg px-3 py-2.5 text-sm text-center transition-colors truncate ${uploadingPhoto ? "bg-slate-100 text-slate-400" : "bg-slate-50 hover:border-[#3CD4E1] hover:text-[#08707A] text-[#4A5568]"}`}>
                    {uploadingPhoto ? tx("Duke ngarkuar...") : form.badge_photo ? tx("Ndrysho foton") : <><i className="fas fa-camera mr-2"></i>{tx("Ngarko JPG")}</>}
                  </div>
                  <input type="file" accept="image/jpeg,.jpg,.jpeg" onChange={handleBadgeUpload} className="hidden" data-testid="badge-upload-free" disabled={uploadingPhoto} />
                </label>
                {form.badge_photo && (
                  <div className="relative w-10 h-10 rounded-lg overflow-hidden border-2 border-[#3CD4E1] shrink-0">
                    <img src={form.badge_photo.startsWith("http") ? form.badge_photo : `${BACKEND_URL}${form.badge_photo}`} alt="Badge preview" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setForm({ ...form, badge_photo: "" })} className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center leading-none">×</button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#4A5568] mb-1">{t("reg_phone")} *</label>
              <PhoneInput
                value={form.phone}
                country={form.phone_country}
                onChange={(v) => setForm({ ...form, phone: v })}
                onCountryChange={(c) => setForm({ ...form, phone_country: c })}
                required
                data-testid="popup-phone"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#4A5568] mb-1">{t("reg_email")} *</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 focus:ring-2 focus:ring-[#3CD4E1]/30 focus:border-[#3CD4E1] outline-none" data-testid="popup-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#4A5568] mb-1">{tx("Qyteti")}</label>
              <input type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder={tx("psh. Tirane")} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 focus:ring-2 focus:ring-[#3CD4E1]/30 focus:border-[#3CD4E1] outline-none" data-testid="popup-city" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#4A5568] mb-1">{tx("Shteti")}</label>
              <CountrySelect value={form.country} onChange={(c) => setForm({ ...form, country: c })} data-testid="popup-country" />
            </div>
          </div>
          {error && <p className="text-sm text-red-500 font-medium" data-testid="popup-error">{error}</p>}
          <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="terms-consent-free">
            <input type="checkbox" checked={form.accepted_terms} onChange={(e) => setForm({ ...form, accepted_terms: e.target.checked })} className="mt-0.5 accent-[#1CA3B0]" />
            <span className="text-[10px] text-[#4A5568] leading-snug">
              {tx("Pranoj")} <a href="/kushtet" target="_blank" rel="noopener noreferrer" className="text-[#1CA3B0] hover:underline font-semibold">{tx("Kushtet e Perdorimit")}</a> {tx("dhe")} <a href="/privatesia" target="_blank" rel="noopener noreferrer" className="text-[#1CA3B0] hover:underline font-semibold">{tx("Politiken e Privatesise")}</a> {tx("te Albanian Aligners Academy. *")}
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="marketing-consent-free">
            <input type="checkbox" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} className="mt-0.5 accent-[#1CA3B0]" />
            <span className="text-[10px] text-[#4A5568] leading-snug">
              {tx("Dua te marr email/SMS promocionale nga Albanian Aligners Academy per evente dhe oferta te reja.")}
            </span>
          </label>
          <button type="submit" disabled={submitting} className="w-full rounded-lg px-6 py-3 font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50 inline-flex items-center justify-center gap-2" style={{ background: "linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)" }} data-testid="popup-submit">
            {submitting ? t('register_processing') : t('register_now')} <i className="fas fa-arrow-right"></i>
          </button>
        </form>
    </Modal>
  );
};

const PaidRegistrationPopup = ({ event, paymentMethods = [], selectedTier = null, onClose }) => {
  const { t, tx } = useLanguage();
  const [form, setForm] = useState({ first_name: "", last_name: "", profession: "", phone: "", phone_country: "al", email: "", city: "", country: "al", badge_photo: "", accepted_terms: false, marketing_consent: false });
  const [method, setMethod] = useState(paymentMethods[0]?.method || "stripe");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const tierPrice = selectedTier?.price || getDisplayPrice(event);
  const [success, setSuccess] = useState(null); // 'bank' | 'cash' | 'pok' after registration
  const [pokOrder, setPokOrder] = useState(null); // { order_id, env }
  const pokContainerRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  // Voucher state
  const [voucherCode, setVoucherCode] = useState("");
  const [voucher, setVoucher] = useState(null); // {code, discount_percent, discount_amount, final_amount, original_amount}
  const [voucherMsg, setVoucherMsg] = useState({ type: "", text: "" });
  const [applyingVoucher, setApplyingVoucher] = useState(false);
  const basePrice = Number(tierPrice) || 0;
  const finalPrice = voucher ? Number(voucher.final_amount) : basePrice;

  // Auto-detect country from user's IP on mount (best-effort, no blocker).
  useEffect(() => {
    let cancelled = false;
    detectCountryFromIP().then((c) => {
      if (!cancelled && c) setForm((f) => ({ ...f, phone_country: c, country: c }));
    });
    return () => { cancelled = true; };
  }, []);

  const handleBadgeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const okType = ["image/jpeg", "image/jpg", "image/pjpeg"].includes(file.type);
    if (!okType || !["jpg", "jpeg"].includes(ext)) {
      setError(tx("Vetem foto JPG/JPEG lejohen."));
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) { setError(tx("Foto max 5MB")); return; }
    setUploadingPhoto(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload?private=true`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, badge_photo: res.data.url }));
    } catch (err) {
      setError(tx("Ngarkimi i fotos deshtoi. Provo perseri."));
    } finally { setUploadingPhoto(false); }
  };

  const iconMap = { "credit-card": "fa-credit-card", paypal: "fa-paypal", bank: "fa-university", cash: "fa-money-bill-wave", pok: "fa-wallet" };
  const selected = paymentMethods.find((m) => m.method === method);

  const finalizePok = async (orderId) => {
    try {
      const res = await axios.post(`${API}/checkout/pok/confirm/${orderId}`);
      // Only close the flow when POK actually reported the payment as paid.
      // If POK is still "pending" (customer closed the widget without paying),
      // we leave the popup open so they can try again.
      if (res?.data?.payment_status !== "paid") return false;
    } catch (err) {
      console.error("POK confirm failed", err);
      return false;
    }
    setSuccess("pok");
    setPokOrder(null);
    return true;
  };

  // Backup polling: some browsers (or user actions like closing 3DS window)
  // prevent POK's onSuccess callback from firing. Poll the confirm endpoint
  // every 4 seconds while the widget is mounted; stop when paid or aborted.
  useEffect(() => {
    if (!pokOrder) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const paid = await finalizePok(pokOrder.order_id);
      if (paid) return; // finalizePok already cleared pokOrder
      setTimeout(tick, 4000);
    };
    const timer = setTimeout(tick, 4000);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pokOrder]);

  // Mount the POK hosted checkout form once we have an order id.
  useEffect(() => {
    if (!pokOrder || !pokContainerRef.current) return;
    if (typeof window === "undefined" || !window.PokPayment) {
      setError(tx("POK SDK nuk u ngarkua. Rifreskoni faqen dhe provoni perseri."));
      setPokOrder(null);
      return;
    }
    try {
      window.PokPayment.renderForm(
        "pok-checkout-container",
        pokOrder.order_id,
        () => finalizePok(pokOrder.order_id),
        (err) => {
          console.error("POK payment error", err);
          setError(err?.message || tx("Pagesa deshtoi. Provoni perseri."));
          setLoading(false);
        },
        {
          env: pokOrder.env || "staging",
          locale: "al",
          initialState: {
            email: form.email,
            holdersName: `${form.first_name} ${form.last_name}`.trim(),
            country: (form.country || "al").toUpperCase(),
            billingCountry: (form.country || "al").toUpperCase(),
            phone: form.phone || "",
            city: form.city || "",
          },
        }
      );
    } catch (err) {
      console.error("POK renderForm error", err);
      setError(tx("Nuk u ngarkua formulari i POK. Provoni perseri."));
      setPokOrder(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pokOrder]);

  const applyVoucher = async () => {
    setVoucherMsg({ type: "", text: "" });
    const code = voucherCode.trim().toUpperCase();
    if (!code) {
      setVoucherMsg({ type: "error", text: tx("Vendos nje kod.") });
      return;
    }
    setApplyingVoucher(true);
    try {
      const res = await axios.post(`${API}/vouchers/validate`, {
        code,
        event_id: event.id,
        tier_label: selectedTier?.label || "",
      });
      if (res.data?.valid) {
        setVoucher(res.data);
        setVoucherMsg({ type: "ok", text: res.data.message || tx("Kodi u aplikua.") });
      } else {
        setVoucher(null);
        setVoucherMsg({ type: "error", text: res.data?.message || tx("Kod i pavlefshem.") });
      }
    } catch (err) {
      setVoucher(null);
      setVoucherMsg({ type: "error", text: err.response?.data?.detail || tx("Gabim gjate validimit.") });
    } finally {
      setApplyingVoucher(false);
    }
  };

  const removeVoucher = () => {
    setVoucher(null);
    setVoucherCode("");
    setVoucherMsg({ type: "", text: "" });
  };

  const handlePay = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.first_name || !form.last_name || !form.email) {
      setError(t("reg_required"));
      return;
    }
    // Phone is now mandatory — need dial code + at least 5 local digits.
    const localDigits = form.phone.replace(/^\+\d+\s*/, "").replace(/\D/g, "");
    if (!form.phone || localDigits.length < 5) {
      setError(tx("Ju lutem plotesoni nje numer telefoni te vlefshem."));
      return;
    }
    if (!form.badge_photo) {
      setError(tx("Ju lutem ngarkoni foton per badge (JPG/JPEG)."));
      return;
    }
    if (!form.accepted_terms) {
      setError(tx("Ju lutem pranoni Kushtet e Perdorimit dhe Politiken e Privatesise per te vazhduar."));
      return;
    }
    setLoading(true);
    try {
      // Online payment via Stripe → redirect to Stripe Checkout
      if (method === "stripe") {
        const res = await axios.post(`${API}/checkout/create`, {
          event_id: event.id,
          origin_url: window.location.origin,
          customer: form,
          tier_label: selectedTier?.label || "",
        });
        window.location.href = res.data.url;
        return;
      }
      // Online payment via POK → create SDK order and mount hosted checkout inline.
      if (method === "pok") {
        const res = await axios.post(`${API}/checkout/pok/create`, {
          event_id: event.id,
          origin_url: window.location.origin,
          customer: form,
          tier_label: selectedTier?.label || "",
          voucher_code: voucher?.code || "",
        });
        setPokOrder({ order_id: res.data.order_id, env: res.data.env });
        setLoading(false);
        return;
      }
      // Offline payment (bank / cash / paypal pending) → create a registration with payment_method
      await axios.post(`${API}/registrations`, { ...form, event_id: event.id, num_participants: 1, payment_method: method, tier_label: selectedTier?.label || "", voucher_code: voucher?.code || "" });
      setSuccess(method);
    } catch (err) {
      setError(err.response?.data?.detail || tx("Ndodhi nje gabim. Provoni perseri."));
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} maxWidth="max-w-lg" testId="paid-reg-popup">
      <div className="flex items-center justify-between p-6 border-b border-slate-200">
        <div>
          <h3 className="text-lg font-bold" style={{ fontFamily: "'Noto Sans', sans-serif" }}>{tx("Regjistrohu & Paguaj")}</h3>
          <p className="text-xs text-[#828D9E] mt-0.5">{event.title}</p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
      </div>

        {success ? (
          <div className="p-7 text-center" data-testid="offline-success">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center bg-[#1CA3B0]/10">
              <i className="fas fa-check text-2xl text-[#1CA3B0]"></i>
            </div>
            <h4 className="font-bold text-lg text-[#003F72] mb-2">{success === "pok" ? tx("Pagesa u krye!") : tx("Regjistrimi u krye!")}</h4>
            <p className="text-sm text-[#4A5568] mb-4 leading-relaxed">
              {success === "bank"
                ? tx("Ekipi yne do t'ju kontaktoje ne email me detajet e plota te transfertes bankare. Pas pageses, ju lutemi na dergoni provën e transfertes.")
                : success === "cash"
                ? tx("Ju jeni regjistruar! Pagesa cash do te kryhet ne diten e eventit. Ekipi yne do t'ju kontaktoje per konfirmim.")
                : success === "pok"
                ? tx("Faleminderit! Pagesa u krye me sukses permes POK. Fatura eshte derguar ne email.")
                : tx("Ekipi yne do t'ju kontaktoje per detajet e pageses.")}
            </p>
            <button onClick={onClose} className="rounded-lg px-6 py-2.5 text-sm font-semibold text-white" style={{ background: "linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)" }}>
              {tx("Mbylle")}
            </button>
          </div>
        ) : pokOrder ? (
          <div className="p-6 space-y-4" data-testid="pok-checkout">
            <div className="bg-[#E6F7F8] rounded-lg p-4 text-center">
              <p className="text-sm text-[#08707A] font-semibold">{tx("Pagesa permes POK")}</p>
              <p className="text-3xl font-bold text-[#003F72]">{formatPrice(finalPrice)}</p>
              {voucher && (
                <p className="text-xs text-[#166534] mt-1">{tx("Kod")} <b>{voucher.code}</b> {tx("— ulje")} {voucher.discount_percent}%</p>
              )}
            </div>
            {error && <p className="text-sm text-red-500 font-medium" data-testid="paid-error">{error}</p>}
            <div id="pok-checkout-container" ref={pokContainerRef} />
            <button
              type="button"
              onClick={() => { setPokOrder(null); setError(""); }}
              className="w-full text-xs text-[#828D9E] hover:text-[#003F72] transition-colors"
              data-testid="pok-cancel"
            >
              &larr; Anulo dhe zgjidh nje metode tjeter pageses
            </button>
          </div>
        ) : (
          <form onSubmit={handlePay} className="p-6 space-y-4">
            <div className="bg-[#E6F7F8] rounded-lg p-4">
              <p className="text-sm text-[#08707A] font-semibold text-center">
                {selectedTier?.label ? `${t('tier_package')}: ${selectedTier.label}` : t('popup_event_price')}
              </p>
              {voucher ? (
                <div className="mt-2 space-y-1 text-sm">
                  <div className="flex items-center justify-between text-[#4A5568]">
                    <span>{tx("Cmimi fillestar")}</span>
                    <span className="line-through">{formatPrice(voucher.original_amount)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[#166534] font-semibold">
                    <span>{tx("Ulje (")}{voucher.discount_percent}%) — {tx("kodi")} <b>{voucher.code}</b></span>
                    <span>− {formatPrice(voucher.discount_amount)}</span>
                  </div>
                  <div className="border-t border-[#B7E4E9] pt-2 mt-1 flex items-center justify-between">
                    <span className="text-xs font-bold uppercase text-[#08707A]">{tx("Total per pagese")}</span>
                    <span className="text-2xl font-bold text-[#003F72]">{formatPrice(voucher.final_amount)}</span>
                  </div>
                </div>
              ) : (
                <p className="text-3xl font-bold text-[#003F72] text-center mt-1">{formatPrice(tierPrice)}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#4A5568] mb-1">{t("reg_first_name")} *</label>
                <input type="text" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 focus:ring-2 focus:ring-[#3CD4E1]/30 focus:border-[#3CD4E1] outline-none" data-testid="paid-first-name" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#4A5568] mb-1">{t("reg_last_name")} *</label>
                <input type="text" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 focus:ring-2 focus:ring-[#3CD4E1]/30 focus:border-[#3CD4E1] outline-none" data-testid="paid-last-name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#4A5568] mb-1">{tx("Profesioni")}</label>
                <input type="text" value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} placeholder={tx("psh. Dentist, Ortodont")} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 focus:ring-2 focus:ring-[#3CD4E1]/30 focus:border-[#3CD4E1] outline-none" data-testid="paid-profession" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#4A5568] mb-1">{tx("Foto per Badge *")}</label>
                <div className="flex items-center gap-2">
                  <label className="flex-1 cursor-pointer min-w-0">
                    <div className={`border border-dashed border-slate-300 rounded-lg px-3 py-2.5 text-sm text-center transition-colors truncate ${uploadingPhoto ? "bg-slate-100 text-slate-400" : "bg-slate-50 hover:border-[#3CD4E1] hover:text-[#08707A] text-[#4A5568]"}`}>
                      {uploadingPhoto ? tx("Duke ngarkuar...") : form.badge_photo ? tx("Ndrysho foton") : <><i className="fas fa-camera mr-2"></i>{tx("Ngarko JPG")}</>}
                    </div>
                    <input type="file" accept="image/jpeg,.jpg,.jpeg" onChange={handleBadgeUpload} className="hidden" data-testid="badge-upload-paid" disabled={uploadingPhoto} />
                  </label>
                  {form.badge_photo && (
                    <div className="relative w-10 h-10 rounded-lg overflow-hidden border-2 border-[#3CD4E1] shrink-0">
                      <img src={form.badge_photo.startsWith("http") ? form.badge_photo : `${BACKEND_URL}${form.badge_photo}`} alt="Badge preview" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => setForm({ ...form, badge_photo: "" })} className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center leading-none">×</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#4A5568] mb-1">{t("reg_phone")} *</label>
                <PhoneInput
                  value={form.phone}
                  country={form.phone_country}
                  onChange={(v) => setForm({ ...form, phone: v })}
                  onCountryChange={(c) => setForm({ ...form, phone_country: c })}
                  required
                  data-testid="paid-phone"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#4A5568] mb-1">{t("reg_email")} *</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 focus:ring-2 focus:ring-[#3CD4E1]/30 focus:border-[#3CD4E1] outline-none" data-testid="paid-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#4A5568] mb-1">{tx("Qyteti")}</label>
                <input type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder={tx("p.sh. Tiranë")} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 focus:ring-2 focus:ring-[#3CD4E1]/30 focus:border-[#3CD4E1] outline-none" data-testid="paid-city" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#4A5568] mb-1">{tx("Shteti")}</label>
                <CountrySelect value={form.country} onChange={(c) => setForm({ ...form, country: c })} data-testid="paid-country" />
              </div>
            </div>

            {/* Payment method selector — inline radio cards */}
            {paymentMethods.length > 0 && (
              <div data-testid="popup-payment-methods">
                <label className="block text-xs font-semibold text-[#4A5568] mb-2">{tx("Metoda e pageses *")}</label>
                <div className="grid grid-cols-1 gap-2">
                  {paymentMethods.map((m) => (
                    <label key={m.method} className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-all ${method === m.method ? "border-[#1CA3B0] bg-[#1CA3B0]/5" : "border-slate-200 bg-white hover:border-[#1CA3B0]/40"}`} data-testid={`pm-${m.method}`}>
                      <input type="radio" name="pm" value={m.method} checked={method === m.method} onChange={() => setMethod(m.method)} className="accent-[#1CA3B0]" />
                      <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(28,163,176,0.1)" }}>
                        <i className={`fas ${iconMap[m.icon] || "fa-credit-card"} text-[#1CA3B0] text-sm`}></i>
                      </span>
                      <span className="text-sm font-semibold text-[#003F72]">{tx(m.label)}</span>
                      {m.method === "pok" && (
                        <span className="ml-auto flex items-center gap-2" data-testid="card-brand-logos" aria-label={tx("Visa and Mastercard accepted")}>
                          <i className="fab fa-cc-visa text-2xl text-[#1A1F71]" title="Visa"></i>
                          <i className="fab fa-cc-mastercard text-2xl text-[#EB001B]" title="Mastercard"></i>
                        </span>
                      )}
                    </label>
                  ))}
                </div>
                {/* Inline details for the selected method */}
                {selected && selected.method === "bank" && selected.details && (
                  <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs space-y-1.5">
                    {selected.details.bank_name && <div className="flex gap-2"><span className="text-[#828D9E] w-20">{tx("Banka:")}</span><span className="text-[#003F72] font-semibold">{selected.details.bank_name}</span></div>}
                    {selected.details.iban && <div className="flex gap-2"><span className="text-[#828D9E] w-20">{tx("IBAN:")}</span><span className="text-[#003F72] font-mono font-semibold break-all">{selected.details.iban}</span></div>}
                    {selected.details.swift && <div className="flex gap-2"><span className="text-[#828D9E] w-20">{tx("SWIFT:")}</span><span className="text-[#003F72] font-mono">{selected.details.swift}</span></div>}
                    {selected.details.account_holder && <div className="flex gap-2"><span className="text-[#828D9E] w-20">{tx("Mbajtesi:")}</span><span className="text-[#003F72]">{selected.details.account_holder}</span></div>}
                  </div>
                )}
                {selected && selected.method === "cash" && selected.details?.instructions && (
                  <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-[#4A5568] whitespace-pre-line">{selected.details.instructions}</div>
                )}
              </div>
            )}

            {error && <p className="text-sm text-red-500 font-medium" data-testid="paid-error">{error}</p>}

            {/* Promo code */}
            <div>
              <label className="block text-xs font-semibold text-[#4A5568] mb-1">Promo Code</label>
              {voucher ? (
                <div className="flex items-center justify-between gap-2 border border-[#166534]/30 bg-[#DCFCE7] rounded-lg px-3 py-2.5" data-testid="voucher-applied">
                  <div className="text-sm">
                    <span className="font-bold text-[#166534]">{voucher.code}</span>
                    <span className="text-[#166534]/80 ml-2">{tx("— aplikuar (")}{voucher.discount_percent}% {tx("ulje")})</span>
                  </div>
                  <button type="button" onClick={removeVoucher} className="text-xs text-red-600 hover:underline font-semibold" data-testid="voucher-remove">{tx("Hiq")}</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={voucherCode}
                    onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyVoucher(); } }}
                    placeholder=""
                    maxLength={32}
                    className="flex-1 border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 focus:ring-2 focus:ring-[#3CD4E1]/30 focus:border-[#3CD4E1] outline-none uppercase tracking-wider font-semibold"
                    data-testid="voucher-input"
                    disabled={applyingVoucher}
                  />
                  <button
                    type="button"
                    onClick={applyVoucher}
                    disabled={applyingVoucher || !voucherCode.trim()}
                    className="rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-50 bg-[#08707A] hover:bg-[#065A63]"
                    data-testid="voucher-apply"
                  >
                    {applyingVoucher ? "..." : tx("Apliko")}
                  </button>
                </div>
              )}
              {voucherMsg.text && (
                <p className={`text-xs mt-1.5 font-medium ${voucherMsg.type === "ok" ? "text-[#166534]" : "text-red-600"}`} data-testid="voucher-message">
                  {voucherMsg.text}
                </p>
              )}
            </div>

            <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="terms-consent-paid">
              <input type="checkbox" checked={form.accepted_terms} onChange={(e) => setForm({ ...form, accepted_terms: e.target.checked })} className="mt-0.5 accent-[#1CA3B0]" />
              <span className="text-[10px] text-[#4A5568] leading-snug">
                {tx("Pranoj")} <a href="/kushtet" target="_blank" rel="noopener noreferrer" className="text-[#1CA3B0] hover:underline font-semibold">{tx("Kushtet e Perdorimit")}</a> {tx("dhe")} <a href="/privatesia" target="_blank" rel="noopener noreferrer" className="text-[#1CA3B0] hover:underline font-semibold">{tx("Politiken e Privatesise")}</a> {tx("te Albanian Aligners Academy. *")}
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="marketing-consent-paid">
              <input type="checkbox" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} className="mt-0.5 accent-[#1CA3B0]" />
              <span className="text-[10px] text-[#4A5568] leading-snug">
                {tx("Dua te marr email/SMS promocionale nga Albanian Aligners Academy per evente dhe oferta te reja.")}
              </span>
            </label>
            <button type="submit" disabled={loading} className="w-full rounded-lg px-6 py-3 font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50 inline-flex items-center justify-center gap-2" style={{ background: "linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)" }} data-testid="paid-submit">
              {loading ? t('register_processing') : ((method === "stripe" || method === "pok") ? `${t('register_continue_payment')} - ${formatPrice(finalPrice)}` : t('register_confirm'))}
              <i className={`fas ${(method === "stripe" || method === "pok") ? "fa-lock" : "fa-check"} ml-1`}></i>
            </button>
            {method === "stripe" && <p className="text-xs text-center text-[#828D9E]"><i className="fas fa-shield-alt mr-1"></i> {tx("Pagesa procesohet ne menyre te sigurte permes Stripe")}</p>}
            {method === "pok" && <p className="text-xs text-center text-[#828D9E]"><i className="fas fa-shield-alt mr-1"></i> {tx("Pagesa procesohet ne menyre te sigurte permes POK Payments")}</p>}
          </form>
        )}
    </Modal>
  );
};

const EventDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const { t, tx } = useLanguage();

  useSEO({
    title: event ? `${event.title} - ${event.date}` : tx("Detaje Eventi"),
    description: event ? `${event.description?.substring(0, 160)}` : tx("Detaje per eventin e Albanian Aligners Academy."),
    keywords: event ? `${event.title}, ${event.speaker}, event dentar, trajnim` : "event dentar",
    ogImage: event?.image_url,
    canonical: event?.slug ? `https://aligners.academy/evente/${event.slug}` : `https://aligners.academy/evente/${id}`,
  });

  // Masterclass has a dedicated, hand-crafted landing page — never render it here.
  useEffect(() => {
    if (id && id.toLowerCase().startsWith("masterclass")) {
      navigate("/evente/masterclass", { replace: true });
    }
  }, [id, navigate]);

  useEffect(() => {
    if (id && id.toLowerCase().startsWith("masterclass")) return;
    axios.get(`${API}/events/${id}`).then((r) => {
      // Same guard if a custom slug was set on a masterclass-type event
      if (r.data?.title?.toLowerCase().includes("modern implantology masterclass")) {
        navigate("/evente/masterclass", { replace: true });
        return;
      }
      // If the visitor came via UUID but the event has a slug, replace the URL
      // with the SEO-friendly slug (no extra fetch — both resolve the same event).
      if (r.data?.slug && id !== r.data.slug) {
        navigate(`/evente/${r.data.slug}`, { replace: true });
      }
      setEvent(r.data);
      // Pre-select the currently active pricing tier (if any)
      setSelectedTier(getActiveTier(r.data));
      setLoading(false);
    }).catch(() => setLoading(false));
    axios.get(`${API}/payment-methods/public`).then((r) => setPaymentMethods(r.data.methods || [])).catch(() => {});
    axios.get(`${API}/trainers`).then((r) => setTrainers(r.data || [])).catch(() => {});
  }, [id, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="text-[#828D9E]">{t("loading")}</p></div>;
  if (!event) return <div className="min-h-screen flex items-center justify-center"><p className="text-[#828D9E]">{t("not_found")}</p></div>;

  const agendaLines = event.agenda ? event.agenda.split("\n").filter((l) => l.trim()) : [];
  const learnLines = event.what_you_learn ? event.what_you_learn.split("\n").filter((l) => l.trim()) : [];
  const activeTier = getActiveTier(event);
  const displayPrice = getDisplayPrice(event);
  const hasPrice = displayPrice && parseFloat(String(displayPrice).replace(",", ".")) > 0;
  const isUpcoming = event.category === "upcoming";
  const tiers = Array.isArray(event.pricing_tiers) ? event.pricing_tiers : [];

  // Find matching trainer for the event's speaker (so we can link to their profile)
  const normName = (s) => (s || "")
    .toLowerCase()
    .replace(/\b(dr|dt|cmdt|prof|dds|phd|md|ms|msc)\.?/g, "")
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const speakerNorm = normName(event.speaker);
  const speakerTrainer = speakerNorm
    ? trainers.find((tr) => {
        const tn = normName(tr.name);
        return tn && (tn === speakerNorm || speakerNorm.includes(tn) || tn.includes(speakerNorm));
      })
    : null;

  return (
    <div data-testid="event-detail-page">
      {/* New Hero — clean white layout: breadcrumb + image left + title/intro right */}
      <section className="bg-white pt-8 pb-10 px-6 md:px-12" data-testid="event-detail-hero">
        <div className="max-w-[1350px] mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="text-[#1CA3B0] hover:underline font-medium">{t("event_breadcrumb_home")}</Link>
            <i className="fas fa-chevron-right text-[9px] text-[#1CA3B0]"></i>
            <Link to="/evente" className="text-[#1CA3B0] hover:underline font-medium">{isUpcoming ? tx("Evente te Ardhshme") : tx("Evente te Kaluara")}</Link>
            <i className="fas fa-chevron-right text-[9px] text-[#828D9E]"></i>
            <span className="text-[#828D9E] truncate">{event.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-10 lg:gap-14 items-start">
            {/* Cover image */}
            <div className="rounded-2xl overflow-hidden bg-slate-100 shadow-sm">
              <img
                src={event.image_url || "https://images.pexels.com/photos/23625648/pexels-photo-23625648.jpeg"}
                alt={event.title}
                className="w-full h-auto object-cover block"
                data-testid="event-detail-image"
              />
            </div>

            {/* Title + intro */}
            <div>
              <span className={`inline-block px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider mb-4 ${isUpcoming ? "bg-[#1CA3B0]/10 text-[#08707A]" : "bg-slate-100 text-[#828D9E]"}`}>
                {isUpcoming ? t("event_upcoming_badge") : t("event_past_badge")}
              </span>
              <h1
                className="font-bold leading-[1.15] mb-6"
                style={{ fontSize: "2rem", color: "#003F72", fontFamily: "'Noto Sans', sans-serif" }}
                data-testid="event-detail-title"
              >
                {event.title}
              </h1>
              {event.description && (
                <p className="text-[#4A5568] leading-relaxed text-base mb-6 whitespace-pre-line">{event.description}</p>
              )}
              {learnLines.length > 0 && (
                <ul className="space-y-3 mb-6" data-testid="event-detail-learn">
                  {learnLines.map((line, i) => (
                    <li key={i} className="flex items-start gap-3 text-[#4A5568] leading-relaxed">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#1CA3B0] mt-2 shrink-0"></span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              )}

              {/* Event meta — vertical stack: speaker, date, location */}
              <div className="pt-6 mt-2 border-t border-slate-100 space-y-3 text-sm" data-testid="event-meta">
                <div className="flex items-center gap-3">
                  <i className="fas fa-user w-4 text-center" style={{ color: "#1d88a8" }}></i>
                  {speakerTrainer ? (
                    <Link
                      to={`/trajnere/${speakerTrainer.slug || speakerTrainer.id}`}
                      className="text-[#08707A] hover:text-[#1CA3B0] underline-offset-2 hover:underline"
                      data-testid="event-speaker-link"
                    >
                      {event.speaker}
                    </Link>
                  ) : (
                    <span className="text-[#4A5568]">{event.speaker}</span>
                  )}
                </div>
                <div className="flex items-center gap-3"><i className="fas fa-calendar-alt w-4 text-center" style={{ color: "#1d88a8" }}></i><span className="text-[#4A5568]">{event.date}{event.schedule ? ` · ${event.schedule}` : ""}</span></div>
                {event.location && (
                  <div className="flex items-center gap-3">
                    <i className="fas fa-map-marker-alt w-4 text-center" style={{ color: "#1d88a8" }}></i>
                    {event.location_url ? (
                      <a href={event.location_url} target="_blank" rel="noopener noreferrer" className="text-[#08707A] hover:text-[#1CA3B0] underline-offset-2 hover:underline" data-testid="event-location-link">
                        {event.location}
                      </a>
                    ) : (
                      <span className="text-[#4A5568]">{event.location}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Price + CTA (only for upcoming events) */}
              {isUpcoming && (
                <div className="mt-10">
                  {tiers.length > 0 && (
                    <div className="mb-6 space-y-2" data-testid="event-pricing-tiers">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#1CA3B0]">{t('tier_choose_package')}</p>
                        <p className="text-[11px] text-[#828D9E]">{t('tier_click_to_select')}</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-stretch">
                        {tiers.map((tier, idx) => {
                          const isActiveTier = activeTier === tier;
                          const isSelected = selectedTier === tier;
                          const inRange = isTierWithinRange(tier);
                          const features = Array.isArray(tier.features)
                            ? tier.features
                            : (tier.features || "").split("\n").map((s) => s.trim()).filter(Boolean);
                          return (
                            <button
                              type="button"
                              key={idx}
                              disabled={!inRange}
                              onClick={() => inRange && setSelectedTier(tier)}
                              className={`text-left rounded-xl border-2 p-4 transition-all flex flex-col ${
                                !inRange
                                  ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
                                  : isSelected
                                  ? "border-[#1CA3B0] bg-[#1CA3B0]/5 shadow-md hover:-translate-y-0.5"
                                  : "border-slate-200 bg-white hover:border-[#1CA3B0]/40 hover:-translate-y-0.5"
                              }`}
                              data-testid={`event-tier-${idx}`}
                              aria-pressed={isSelected}
                              aria-disabled={!inRange}
                            >
                              {/* Header row — title + radio (inline, aligned across cards) */}
                              <div className="flex items-center justify-between gap-2 mb-2 min-h-[24px]">
                                <div className="flex items-center gap-2 min-w-0">
                                  <p className={`text-xs font-bold uppercase tracking-wider truncate ${isSelected ? "text-[#08707A]" : "text-[#4A5568]"}`}>
                                    {tier.label || `${tx("Fazë")} ${idx + 1}`}
                                  </p>
                                  {isActiveTier && inRange && (
                                    <span className="inline-block rounded-full bg-[#1CA3B0] text-white text-[9px] font-bold px-2 py-0.5 uppercase tracking-wider whitespace-nowrap" data-testid={`event-tier-${idx}-now`}>
                                      {t('tier_now_badge')}
                                    </span>
                                  )}
                                  {!inRange && (
                                    <span className="inline-block rounded-full bg-slate-200 text-slate-500 text-[9px] font-bold px-2 py-0.5 uppercase tracking-wider whitespace-nowrap" data-testid={`event-tier-${idx}-expired`}>
                                      {t('tier_expired_badge')}
                                    </span>
                                  )}
                                </div>
                                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                                  isSelected ? "border-[#1CA3B0] bg-[#1CA3B0]" : "border-slate-300 bg-white"
                                }`}>
                                  {isSelected && <i className="fas fa-check text-white text-[9px]"></i>}
                                </span>
                              </div>
                              {/* Price */}
                              <div className="mb-3">
                                <span className={`font-bold ${isSelected ? "text-[#08707A]" : "text-[#0A0D10]"}`} style={{ fontSize: "1.6rem", fontFamily: "'Noto Sans', sans-serif", lineHeight: 1.1 }}>
                                  {tier.price ? formatPrice(tier.price) : "-"}
                                </span>
                              </div>
                              {/* Dates */}
                              {(tier.start_date || tier.end_date) && (
                                <p className="text-[11px] text-[#828D9E] mb-3 flex items-center gap-1.5">
                                  <i className="fas fa-calendar-alt text-[10px]"></i>
                                  {tier.start_date}{tier.end_date ? ` - ${tier.end_date}` : ""}
                                </p>
                              )}
                              {/* Features */}
                              {features.length > 0 && (
                                <ul className="space-y-1.5" data-testid={`event-tier-${idx}-features`}>
                                  {features.map((f, fi) => (
                                    <li key={fi} className="flex items-start gap-2 text-[13px] text-[#4A5568] leading-snug">
                                      <i className={`fas fa-check mt-1 text-[10px] shrink-0 ${isSelected ? "text-[#1CA3B0]" : "text-[#08707A]"}`}></i>
                                      <span>{f}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {hasPrice && tiers.length === 0 && (
                    <div className="mb-5">
                      <span className="font-bold" style={{ fontSize: "2rem", color: "#08707A", fontFamily: "'Noto Sans', sans-serif", lineHeight: 1.1 }}>{formatPrice(displayPrice)}</span>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => setShowPopup(hasPrice ? "paid" : "free")}
                      className="inline-flex items-center justify-center gap-2 rounded-xl px-7 py-3.5 font-semibold text-white text-sm transition-all hover:-translate-y-0.5 shadow-md hover:shadow-lg"
                      style={{ background: "linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)" }}
                      data-testid="register-btn"
                    >
                      <i className="fas fa-user-plus"></i>
                      {t('register_now')}{selectedTier?.price ? ` - ${formatPrice(selectedTier.price)}` : (tiers.length === 0 && displayPrice ? ` - ${formatPrice(displayPrice)}` : "")}
                    </button>
                    {Array.isArray(event.days) && event.days.length > 0 && (
                      <button
                        onClick={() => document.querySelector("[data-testid=event-days]")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                        className="inline-flex items-center justify-center gap-2 rounded-xl px-7 py-3.5 font-semibold text-sm transition-all hover:-translate-y-0.5 border-2"
                        style={{ borderColor: "#1CA3B0", color: "#1CA3B0", background: "white" }}
                        data-testid="view-modules-btn"
                      >
                        <i className="fas fa-list-ul"></i> {tx("Shiko Modulet")}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-6 md:px-12 max-w-[1350px] mx-auto">
        <div className="grid grid-cols-1 gap-12">
          <div className="space-y-10">
            {event.who_for && (
              <div>
                <h2 className="text-xl font-bold mb-2" style={{ color: "rgb(28,163,176)", fontFamily: "'Noto Sans', sans-serif" }}>{t("event_who_for")}</h2>
                <hr className="mb-4" style={{ borderColor: "rgba(28,163,176,0.1)" }} />
                <p className="text-[#4A5568] leading-relaxed">{event.who_for}</p>
              </div>
            )}
            {agendaLines.length > 0 && (
              <div>
                <h2 className="text-xl font-bold mb-2" style={{ color: "rgb(28,163,176)", fontFamily: "'Noto Sans', sans-serif" }}>{t("event_agenda")}</h2>
                <hr className="mb-4" style={{ borderColor: "rgba(28,163,176,0.1)" }} />
                <div className="space-y-3">{agendaLines.map((line, i) => (<div key={i} className="flex items-start gap-3 text-[#4A5568] py-2 border-b border-slate-100 last:border-0"><i className="fas fa-clock mt-1 text-sm" style={{ color: "#1d88a8" }}></i>{line}</div>))}</div>
              </div>
            )}
            {/* Days breakdown — Informacion i Detajuar i Eventit / TEMATIKAT */}
            {Array.isArray(event.days) && event.days.length > 0 && (
              <div data-testid="event-days">
                <h2 className="text-xl font-bold mb-2" style={{ color: "#003F72", fontFamily: "'Noto Sans', sans-serif" }}>{tx("Informacion i Detajuar i Eventit")}</h2>
                <hr className="mb-6" style={{ borderColor: "rgba(28,163,176,0.1)" }} />
                <div className="bg-[#f8fafb] rounded-2xl p-6 sm:p-8">
                  <p className="text-xs font-bold uppercase tracking-[0.15em] mb-6" style={{ color: "#1CA3B0" }}>{t('event_topics_label')}:</p>
                  <div className={`grid grid-cols-1 gap-6 ${event.days.length > 1 ? "md:grid-cols-2" : ""}`}>
                    {event.days.map((d, idx) => (
                      <div key={idx} className="bg-white rounded-xl p-6 border border-slate-100" data-testid={`event-day-${idx}`}>
                        <h3 className="text-base font-bold mb-4" style={{ color: "#1CA3B0", fontFamily: "'Noto Sans', sans-serif" }}>{d.title || `DITA ${idx + 1}`}</h3>
                        <ul className="space-y-3">
                          {(d.topics || []).map((tp, i) => {
                            const item = typeof tp === "string" ? { title: tp, description: "" } : (tp || {});
                            const descLines = (item.description || "")
                              .split("\n")
                              .map((s) => s.trim())
                              .filter(Boolean);
                            return (
                              <li key={i} className="text-sm text-[#4A5568] leading-relaxed" data-testid={`event-day-${idx}-topic-${i}`}>
                                {item.title && (
                                  <div className="flex items-start gap-2.5">
                                    <span className="text-[#1CA3B0] font-bold shrink-0">{i + 1})</span>
                                    <span className="font-semibold text-[#0A0D10]">{item.title}</span>
                                  </div>
                                )}
                                {descLines.length > 0 && (
                                  <ul className="mt-1.5 ml-7 space-y-1.5">
                                    {descLines.map((line, li) => (
                                      <li key={li} className="flex items-start gap-2 text-[13px] text-[#4A5568]">
                                        <span className="text-[#1CA3B0] mt-1.5 w-1 h-1 rounded-full bg-[#1CA3B0] shrink-0"></span>
                                        <span>{line}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Event Gallery link (only if there are images or videos) */}
            {((event.gallery_images && event.gallery_images.length > 0) || (event.gallery_videos && event.gallery_videos.length > 0)) && (
              <div data-testid="event-gallery-link">
                <h2 className="text-xl font-bold mb-2" style={{ color: "rgb(28,163,176)", fontFamily: "'Noto Sans', sans-serif" }}>{tx("Galeria")}</h2>
                <hr className="mb-5" style={{ borderColor: "rgba(28,163,176,0.1)" }} />
                <Link
                  to={`/galeria?album=event-${event.id}`}
                  className="group block rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-lg transition-shadow"
                  data-testid="event-gallery-link-btn"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-0">
                    <div className="relative h-40 sm:h-full bg-slate-100 overflow-hidden">
                      <img
                        src={(event.gallery_images && event.gallery_images[0]) || event.image_url}
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/10 to-transparent" />
                      <i className="fas fa-images absolute top-3 left-3 text-white text-lg drop-shadow"></i>
                    </div>
                    <div className="p-5 sm:p-6 flex items-center justify-between gap-4">
                      <div>
                        <p className="font-bold text-[#003F72] text-base sm:text-lg mb-1" style={{ fontFamily: "'Noto Sans', sans-serif" }}>
                          {tx("Shiko galerine e plote")}
                        </p>
                        <p className="text-sm text-[#4A5568]">
                          {(event.gallery_images || []).length} foto
                          {(event.gallery_videos || []).length > 0 ? ` · ${(event.gallery_videos || []).length} video` : ""}
                          {" "}nga ky event
                        </p>
                      </div>
                      <span className="w-10 h-10 rounded-full flex items-center justify-center bg-[#1CA3B0]/10 text-[#1CA3B0] group-hover:bg-[#1CA3B0] group-hover:text-white transition-colors shrink-0">
                        <i className="fas fa-arrow-right text-sm"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Popups */}
      {showPopup === "free" && <FreeRegistrationPopup event={event} selectedTier={selectedTier} onClose={() => setShowPopup(null)} />}
      {showPopup === "paid" && <PaidRegistrationPopup event={event} paymentMethods={paymentMethods} selectedTier={selectedTier} onClose={() => setShowPopup(null)} />}

      {/* Final CTA — full-width, animated gradient, left text + right button */}
      {isUpcoming && (
        <section className="px-6 md:px-12 pb-20" data-testid="event-final-cta">
          <style>{`
            @keyframes aaaGradientShift {
              0%   { background-position: 0% 50%; }
              50%  { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
            .aaa-cta-anim {
              background: linear-gradient(120deg, #3CD4E1 0%, #1CA3B0 25%, #003F72 50%, #1CA3B0 75%, #3CD4E1 100%);
              background-size: 300% 300%;
              animation: aaaGradientShift 12s ease-in-out infinite;
            }
          `}</style>
          <div className="max-w-[1350px] mx-auto rounded-2xl px-8 py-10 md:px-14 md:py-12 text-white relative overflow-hidden aaa-cta-anim">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
              <div className="flex-1 min-w-0">
                <h3 className="text-2xl md:text-3xl font-bold mb-2 text-white" style={{ fontFamily: "'Noto Sans', sans-serif" }}>
                  {tx("Mos e humbisni kete event!")}
                </h3>
                <p className="text-white/90 text-base md:text-lg">{tx("Vendet jane te limituara!")}</p>
              </div>
              <div className="shrink-0">
                <button
                  onClick={() => setShowPopup(hasPrice ? "paid" : "free")}
                  className="inline-flex items-center justify-center gap-2 rounded-xl px-8 py-3.5 font-semibold text-sm bg-white transition-all hover:-translate-y-0.5 shadow-md hover:shadow-xl"
                  style={{ color: "#003F72" }}
                  data-testid="event-final-cta-btn"
                >
                  <i className="fas fa-user-plus"></i> {t('register_now_full')}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default EventDetailPage;
export { PaidRegistrationPopup };
